import Moment from "moment";
import _ from "lodash/lodash";
import CalendarLayout from "./calendar-layout.model";
import CalendarItem from "./calendar-item.model";

export default class Calendar {

    constructor(calendar) {
        this.cols = [];
        this.colsByItem = {};
        this.rows = [];
        this.rowsByItem = {};
        this.cells = null;
        this.layouts = null;

        this._date = calendar && calendar.date ? Moment(calendar.date) : Moment();
        this._items = calendar && calendar.items || [];
        this.layoutColRatio = calendar && calendar.layoutColRatio || 1;
    }

    onInitComplete() {
        this.cells = null;
        this.layouts = null;

        this.updateTitles();
        this.items = this._items;
    }

    get date() {
        return this._date;
    }

    set date(date) {
        let oldDate = this._date;
        this._date = Moment(date);
        this.onDateUpdated(oldDate);
        this.updateTitles();

        this.layouts = null;
        this.cells = null;
    }

    get items() {
        return this._items;
    }

    set items(items) {
        this.clearItems();
        this.addItems(items);
    }

    addItems(items) {
        items.forEach(item => this.addItem(item));
    }

    addItem(item) {
        this.items.push(item);

        let col = this.colForItem(item);
        let row = this.rowForItem(item);

        if (!col || !row) {
            return;
        }

        col.add(item);
        row.add(item);

        this.cells = null;
        this.layouts = null;
    }

    removeItem(item) {
        _(this.items).remove(_item => _item.id == item.id);

        let col = this.colForItem(item);
        if (col) {
            col.remove(item);
        }

        let row = this.rowForItem(item);
        if (row) {
            row.remove(item);
        }

        this.layouts = null;

        delete this.colsByItem[item.uuid];
        delete this.rowsByItem[item.uuid];

        let items = this.cellItems(col, row);
        if (items) {
            _(items).remove(_item => _item.id == item.id);
        }
    }

    clearItems() {
        this.cells = null;
        this.layouts = null;
        this._items = [];
        this.colsByItem = {};
        this.rowsByItem = {};

        this.rows.forEach(row => {
            row.clearItems();
        });

        this.cols.forEach(col => {
            col.clearItems();
        });
    }

    onDateUpdated() {
        // Nothing to do.
    }

    cellKey(col, row) {
        return col.id + "-" + row.id;
    }

    cellItems(col, row) {
        if (!this.cells) {
            this.updateCells();
        }

        return this.cells[this.cellKey(col, row)];
    }

    itemLayout(item) {
        if (!this.layouts) {
            this.updateLayouts();
        }

        return this.layouts[item.uuid];
    }

    updateCells() {
        this.cells = {};

        this.items.forEach(item => {
            let col = this.colForItem(item);
            let row = this.rowForItem(item);

            if (col != null && row != null) {
                let cellKey = this.cellKey(col, row);
                let cellItems = this.cells[cellKey];
                if (!cellItems) {
                    this.cells[cellKey] = cellItems = [];
                }

                cellItems.push(item);
            }
        });
    }

    updateLayouts() {
        this.layouts = {};

        this.items.forEach(item => {
            let col = this.colForItem(item);
            let row = this.rowForItem(item);

            if (col != null && row != null) {
                let layout = new CalendarLayout();
                layout.rowOffset = this.layoutRowOffset(item, col, row);
                this.layouts[item.uuid] = layout;
            }
        });

        let overlappingItemGroups = CalendarItem.groupByOverlapping(this.items);

        overlappingItemGroups.forEach(items => {
            let colIndex = 0;

            items.forEach(item => {
                let layout = this.layouts[item.uuid];

                if (layout) {
                    layout.colIndex = colIndex++;
                    layout.colCount = items.length;
                }
            });
        });
    }

    updateTitles() {
        this.cols.forEach(col => col.title = this.formatColTitle(col));
        this.rows.forEach(row => row.title = this.formatRowTitle(row));
    }

    layoutRowOffset(item, col, row) {
        let colDate = Moment(col.date).startOf(col.timeUnit);
        let rowDate = Moment(row.date).startOf(row.timeUnit);
        let date = colDate.add(rowDate.get(row.timeUnit), row.timeUnit);

        let offset = item.start.diff(date, 'minutes');
        return offset;
    }

    colForItem(item) {
        let col = this.colsByItem[item.uuid];
        if (col) {
            return col;
        }

        col = _(this.cols).find(col => {
            let itemKey = item.start.get(col.timeUnit);
            let colKey = col.date.get(col.timeUnit);
            return itemKey == colKey;
        });

        if (col) {
            this.colsByItem[item.uuid] = col;
        }

        return col;
    }

    rowForItem(item) {
        let row = this.rowsByItem[item.uuid];
        if (row) {
            return row;
        }

        row = _(this.rows).find(row => {
            let itemKey = item.start.get(row.timeUnit);
            let rowKey = row.date.get(row.timeUnit);
            return itemKey == rowKey;
        });

        if (row) {
            this.rowsByItem[item.uuid] = row;
        }

        return row;
    }
}

/**
 * Creates a new UUID.
 *
 * @returns {string}
 */
export function

UUID() {
    let d = new Date().getTime();
    let uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        let r = (d + Math.random() * 16) % 16 | 0;
        d = Math.floor(d / 16);
        return (c == 'x' ? r : (r & 0x3 | 0x8)).toString(16);
    });

    return uuid;
}



// WEBPACK FOOTER //
// ./src/modules/main/components/calendar/model/calendar.model.js